import {
  addMonths,
  dayNumber,
  daysInMonth,
  isWorkday,
  nthWeekdayOfMonth,
  nthWorkdayOfMonth,
  weekday,
  weekStart,
  ymd,
} from "./calendar";
import type { Rule } from "./types";

/** Safety cap for searches over periods that may have no candidate (e.g. "every 5th friday"). */
const MAX_PERIODS = 400;

const hasMonthSpecifics = (r: Rule) => !!(r.monthDays?.length || r.nthWeekdays?.length || r.nthWorkdays?.length);
const hasYearSpecifics = (r: Rule) => !!(r.yearDates?.length || r.yearNth?.length);

function monthCandidates(rule: Rule, y: number, m: number): number[] {
  const out = new Set<number>();
  const len = daysInMonth(y, m);
  for (const d of rule.monthDays ?? []) out.add(dayNumber(y, m, d < 0 ? len + d + 1 : Math.min(d, len)));
  for (const { n, wd } of rule.nthWeekdays ?? []) {
    const d = nthWeekdayOfMonth(y, m, n, wd);
    if (d !== null) out.add(d);
  }
  for (const n of rule.nthWorkdays ?? []) {
    const d = nthWorkdayOfMonth(y, m, n);
    if (d !== null) out.add(d);
  }
  return [...out].sort((a, b) => a - b);
}

function yearCandidates(rule: Rule, y: number): number[] {
  const out = new Set<number>();
  for (const { m, d } of rule.yearDates ?? []) out.add(dayNumber(y, m, Math.min(d, daysInMonth(y, m))));
  for (const { m, n, wd } of rule.yearNth ?? []) {
    const d = nthWeekdayOfMonth(y, m, n, wd);
    if (d !== null) out.add(d);
  }
  return [...out].sort((a, b) => a - b);
}

function nextWorkday(dn: number): number {
  let d = dn;
  while (!isWorkday(d)) d++;
  return d;
}

/** Earliest occurrence on or after `from` (the first period is the one containing `from`). */
export function firstOnOrAfter(rule: Rule, from: number): number {
  switch (rule.freq) {
    case "hour":
    case "day":
      return rule.workdays ? nextWorkday(from) : from;
    case "week": {
      if (!rule.weekdays?.length) return from;
      let d = from;
      while (!rule.weekdays.includes(weekday(d))) d++;
      return d;
    }
    case "month": {
      if (!hasMonthSpecifics(rule)) return from;
      const { y, m } = ymd(from);
      for (let i = 0; i < MAX_PERIODS; i++) {
        const total = y * 12 + m - 1 + i;
        const found = monthCandidates(rule, Math.floor(total / 12), (total % 12) + 1).find((d) => d >= from);
        if (found !== undefined) return found;
      }
      return from;
    }
    case "year": {
      if (!hasYearSpecifics(rule)) return from;
      const { y } = ymd(from);
      for (let i = 0; i < MAX_PERIODS; i++) {
        const found = yearCandidates(rule, y + i).find((d) => d >= from);
        if (found !== undefined) return found;
      }
      return from;
    }
  }
}

/** The occurrence following `prev`, honoring the interval (periods are counted from `prev`'s). */
export function nextAfter(rule: Rule, prev: number, startDay: number): number {
  const n = rule.interval;
  switch (rule.freq) {
    case "hour":
    case "day": {
      if (!rule.workdays) return prev + n;
      let d = prev;
      for (let i = 0; i < n; i++) d = nextWorkday(d + 1);
      return d;
    }
    case "week": {
      if (!rule.weekdays?.length) return prev + 7 * n;
      const start = weekStart(prev, startDay);
      const offsets = rule.weekdays.map((wd) => (wd - startDay + 7) % 7).sort((a, b) => a - b);
      const same = offsets.map((o) => start + o).find((d) => d > prev);
      if (same !== undefined) return same;
      return start + 7 * n + (offsets[0] ?? 0);
    }
    case "month": {
      if (!hasMonthSpecifics(rule)) return addMonths(prev, n);
      const { y, m } = ymd(prev);
      const same = monthCandidates(rule, y, m).find((d) => d > prev);
      if (same !== undefined) return same;
      for (let i = 1; i < MAX_PERIODS; i++) {
        const total = y * 12 + m - 1 + i * n;
        const c = monthCandidates(rule, Math.floor(total / 12), (total % 12) + 1)[0];
        if (c !== undefined) return c;
      }
      return addMonths(prev, n);
    }
    case "year": {
      if (!hasYearSpecifics(rule)) return addMonths(prev, 12 * n);
      const { y } = ymd(prev);
      const same = yearCandidates(rule, y).find((d) => d > prev);
      if (same !== undefined) return same;
      for (let i = 1; i < MAX_PERIODS; i++) {
        const c = yearCandidates(rule, y + i * n)[0];
        if (c !== undefined) return c;
      }
      return addMonths(prev, 12 * n);
    }
  }
}
